import { VesselsNotFoundSearchIcon } from "../../shared/Icons/VesselsNotFoundSearchIcon";
import { themeAtom } from "../../shared/atoms";
import { VESSEL_SEARCH_SORT_COLUMNS } from "../models/advanced-vessel-search.constants";
import { AdvancedVesselSortField } from "../models/advanced-vessel-search.models";
import { TimeCharter, VesselDetails } from "../models/search.interfaces";
import { SearchVesselsTableRow } from "./SearchVesselsTableRow";
import { useAtomValue } from "jotai";
import React, { Dispatch, SetStateAction } from "react";
import { twMerge } from "tailwind-merge";

const Th = ({ children, className = "" }: { children?: React.ReactNode; className?: string }) => {
  return <th className={twMerge(`h-14 px-4 py-2 text-[13px] font-semibold ${className}`)}>{children}</th>;
};

type SearchVesselsTableProps = {
  /** null when data is loading, empty array when no data found */
  data: VesselDetails[] | null;
  searchQuery: string;
  openIndex: number | null;
  toggleAccordion: (toggleIndex: number, item: VesselDetails) => void;
  showSelectedOnly: boolean;
  fullName: string;
  handleVesselNameClick: (item: VesselDetails) => void;
  timeCharters: TimeCharter[];
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  sortBy?: AdvancedVesselSortField;
  sortDirection?: "asc" | "desc";
  onSort: (field: AdvancedVesselSortField) => void;
  hasAdvancedSearch: boolean;
};

export function SearchVesselsTable({
  data,
  searchQuery,
  openIndex,
  toggleAccordion,
  showSelectedOnly,
  fullName,
  handleVesselNameClick,
  timeCharters,
  setIsLoading,
  sortBy,
  sortDirection,
  onSort,
  hasAdvancedSearch,
}: SearchVesselsTableProps) {
  const theme = useAtomValue(themeAtom);
  return (
    <table className="min-w-full">
      {/* border-separate and spacing 0 doesn't seem to work, using pseudo element makes the job */}
      <thead
        className={twMerge(
          "sticky top-0 uppercase after:pointer-events-none after:absolute after:inset-0 after:border-y",
          theme.headerComponent.notifications.drawerBackground,
          theme.headerComponent.notifications.headerText,
          theme.myVesselsTable.tailwind.tableBorder,
          theme.myVesselsTable.tailwind.headBorderY,
        )}
      >
        <tr className="text-left text-sm">
          {VESSEL_SEARCH_SORT_COLUMNS.map((column) => (
            <th
              key={column.field}
              className="h-14 px-4 py-2 text-[13px] font-semibold"
              aria-sort={sortBy === column.field ? (sortDirection === "desc" ? "descending" : "ascending") : "none"}
            >
              <button
                type="button"
                onClick={() => onSort(column.field)}
                data-test={`search-vessels-sort-${column.field}`}
                className="flex items-center gap-1 text-left uppercase focus-visible:outline-green"
              >
                {column.label}
                {sortBy === column.field && <span aria-hidden="true">{sortDirection === "desc" ? "↓" : "↑"}</span>}
              </button>
            </th>
          ))}
          <Th />
        </tr>
      </thead>

      <tbody>
        {(() => {
          if (data === null) {
            return <tr className="h-96 border-b border-gray-300 bg-white" />;
          }

          if (data.length === 0) {
            return (
              <tr className="h-96">
                <td colSpan={9}>
                  <div className="flex flex-col items-center justify-center gap-4 text-center">
                    <VesselsNotFoundSearchIcon
                      outerFill={theme.myVesselsTable.searchIcon.outerFill}
                      innerFill={theme.myVesselsTable.searchIcon.innerFill}
                      glyphFill={theme.myVesselsTable.searchIcon.glyphFill}
                    />

                    <div className={twMerge(theme.myVesselsTable.tailwind.textDefault)}>
                      <p>
                        {hasAdvancedSearch
                          ? "No vessels match your search and filters."
                          : `Your search “${searchQuery}” didn’t match any vessel.`}{" "}
                        <br />
                        Please try again.
                      </p>
                    </div>
                  </div>
                </td>
              </tr>
            );
          }

          return data.map((item, index) => (
            <SearchVesselsTableRow
              index={index}
              key={index}
              item={item}
              isOpen={index === openIndex}
              toggleAccordion={() => toggleAccordion(index, item)}
              showSelectedOnly={showSelectedOnly}
              fullName={fullName}
              handleVesselNameClick={handleVesselNameClick}
              timeCharters={timeCharters}
              setIsLoading={setIsLoading}
            />
          ));
        })()}
      </tbody>
    </table>
  );
}
